import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { confirmAlert } from "react-confirm-alert";
import { findByEvento, remove, save } from "./api-evento-turma";
import TurmaSelectDialog from "../../Turma/TurmaSelectDialog";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { errorToast } from "../../../util/ToastUtil";
import Loading from "../../UI/Loading";

export default function EventoTurmaTable({ evento }) {
  const [eventoTurmas, setEventoTurmas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openSearchTurmas, setOpenSearchTurmas] = useState(false);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    findByEvento(evento.codigo)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
        } else {
          setEventoTurmas(data);
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [evento.codigo]);

  const handleTurmasSelected = (turmas) => {
    if (!turmas || turmas.length === 0) return;
    Promise.all(turmas.map((turma) => save({ evento, turma }).then((data) => ({ turma, data })))).then((results) => {
      const salvas = results.filter(({ data }) => data && !data.error).map(({ data }) => data);
      const duplicadas = results.filter(({ data }) => data?.error).map(({ turma }) => turma.descricao);
      if (salvas.length > 0) setEventoTurmas((prevState) => [...prevState, ...salvas]);
      if (duplicadas.length > 0) errorToast(`Turma(s) já cadastrada(s): ${duplicadas.join(", ")}.`);
    });
  };

  const handleDelete = (eventoTurma) =>
    confirmAlert({
      title: "Alerta",
      message: `Deseja remover a turma ${eventoTurma.turma.descricao} do evento?`,
      buttons: [
        {
          label: "Sim",
          onClick: () => {
            remove(eventoTurma.codigo).then((data) => {
              if (data?.error) {
                errorMessage(data.message);
                return;
              }
              setEventoTurmas((prevState) => prevState.filter((et) => et.codigo !== eventoTurma.codigo));
            });
          },
        },
        { label: "Não", onClick: () => {} },
      ],
    });

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2 }}>
      <TurmaSelectDialog
        openSearchTurmas={openSearchTurmas}
        setOpenSearchTurmas={setOpenSearchTurmas}
        setTurmasSelected={handleTurmasSelected}
      />

      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} p={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            Turmas
          </Typography>
          <Chip size="small" label={eventoTurmas.length} />
        </Stack>
        {!evento.enviado && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setOpenSearchTurmas(true)}>
            Adicionar
          </Button>
        )}
      </Stack>
      <Divider />

      {isLoading ? (
        <Box py={4}>
          <Loading size={28} description="Carregando turmas..." />
        </Box>
      ) : eventoTurmas.length === 0 ? (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={4} px={2}>
          Nenhuma turma adicionada.
        </Typography>
      ) : (
        <List dense disablePadding sx={{ maxHeight: 420, overflowY: "auto" }}>
          {eventoTurmas.map((et) => (
            <ListItem
              key={et.codigo}
              divider
              secondaryAction={
                !evento.enviado && (
                  <Tooltip title="Remover turma">
                    <IconButton edge="end" size="small" onClick={() => handleDelete(et)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )
              }
            >
              <ListItemText primary={`${et.turma.curso?.abreviacao ?? ""} ${et.turma.descricao}`.trim()} />
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
}
